import { useReducer, useState } from "react";
import { ApolloError, gql, OperationVariables, useQuery } from "@apollo/client";
import { useApolloClient } from "@apollo/client";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import { Option } from "components/Select/Select";
import { INSTALLATION_CURRENCY } from "config";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContactIdData } from "providers/ContactIdProvider";
import { tradableTag } from "services/permissions/trade";
import { getBackendTranslation } from "utils/backTranslations";
import { SecurityTypeCode, SecuritySubTypeCode } from "../holdings/types";
import { getFetchPolicyOptions } from "../utils";

const TRADABLE_SECURITIES_QUERY = gql`
  query GetTradableSecurities(
    $currency: String
    $countryCode: String
    $securityType: String
    $name: String
    $tradableTag: [String]
    $securitySubType: String
  ) {
    securities(
      tags: $tradableTag
      countryCode: $countryCode
      securityType: $securityType
      securitySubType: $securitySubType
      name: $name
    ) {
      id
      name
      namesAsMap
      isinCode
      securityCode
      url
      url2
      country {
        id
        name
        code
        namesAsMap
      }
      latestMarketData {
        id
        date: obsDate
        price: closeView
      }
      type {
        id
        name
        code
        namesAsMap
      }
      securitySubType {
        id
        code
        name: value
        __typename
      }
      currency {
        id
        securityCode
      }
      profile {
        twrYTD: attribute(attributeKey: "morningstar.FUND.perfYTD") {
          defaultValue
        }
        twr3Y: attribute(attributeKey: "morningstar.FUND.perf3Y") {
          defaultValue
        }
      }
      managementFee
      minTradeAmount
      fxRate(quoteCurrency: $currency)
    }
  }
`;

interface SecurityPrice {
  id: number;
  price: number;
  date: string;
}

export interface TradableSecurity {
  id: number;
  name: string;
  namesAsMap: Record<string, string>;
  securityCode: string;
  isinCode?: string;
  url: string;
  url2: string;
  latestMarketData?: SecurityPrice;
  currency: {
    securityCode: string;
  };
  country?: {
    code: string;
    name: string;
    namesAsMap: Record<string, string>;
  };
  type: {
    code: SecurityTypeCode;
    name: string;
    namesAsMap: Record<string, string>;
  };
  securitySubType?: {
    code: SecuritySubTypeCode;
    name: string;
    namesAsMap: Record<string, string>;
  };
  profile?: {
    twrYTD: {
      defaultValue: number | null;
    };
    twr3Y: {
      defaultValue: number | null;
    };
  };
  managementFee: number;
  minTradeAmount: number;
  fxRate: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  analytics: {
    grouppedAnalytics: {
      grouppedAnalyticsTimePeriod: {
        timePeriodCode: string;
        performance: number;
      }[];
    };
  };
}

export interface TradableSecuritiesQuery {
  securities: TradableSecurity[];
}

interface SecurityFilterOption {
  id: string | null;
  label: string;
}

interface TradableSecuritiesFilters {
  country: Option;
  type: Option;
  subType: Option;
  name: string;
}

const filtersReducer = (
  filters: TradableSecuritiesFilters,
  newFilters: Partial<TradableSecuritiesFilters>
) => ({ ...filters, ...newFilters });

const emptyOption: SecurityFilterOption = {
  id: null,
  label: "-",
};

//initial values for filtering select & input components (empty)
const initialFilters = {
  country: emptyOption,
  type: emptyOption,
  subType: emptyOption,
  name: "",
};

//initial selectable options for filtering select components (empty)
const filterOptionsInitial = {
  country: [emptyOption],
  type: [emptyOption],
  securitySubType: [emptyOption],
};

export const useGetTradebleSecurities = (currencyCode: string) => {
  const { selectedContactId } = useGetContactIdData();
  const {
    data: { portfoliosCurrency } = {
      portfoliosCurrency: INSTALLATION_CURRENCY,
    },
  } = useGetContactInfo(false, selectedContactId);
  const { i18n, t } = useModifiedTranslation();
  const locale = i18n.language;
  const [filters, setFilters] = useReducer(filtersReducer, initialFilters);

  const { loading, error, data } = useQuery<TradableSecuritiesQuery>(
    TRADABLE_SECURITIES_QUERY,
    {
      variables: {
        countryCode: filters.country.id,
        securityType: filters.type.id,
        securitySubType: filters.subType.id,
        name: "",
        currency: currencyCode ?? portfoliosCurrency,
        tradableTag,
      },
      ...getFetchPolicyOptions(
        `useGetTradebleSecurities.${filters.country.id}.${filters.type.id}.${filters.subType.id}`
      ),
    }
  );

  const dataWithSubNames = data?.securities?.map((sec) => ({
    ...sec,
    securitySubType: sec.securitySubType
      ? {
          ...sec.securitySubType,
          namesAsMap: t(`securitySubTypes.${sec.securitySubType?.code}`, {
            defaultValue: "",
          }),
        }
      : undefined,
  }));
  const filterOptions =
    dataWithSubNames?.reduce((prev, curr) => {
      const securityCountry = curr.country;
      if (
        securityCountry &&
        !prev.country.some((country) => country.id === securityCountry.code)
      ) {
        prev.country.push({
          id: securityCountry.code,
          label: t(`fundTypes.${securityCountry.code}`, {
            value: securityCountry.name,
            defaultValue: getBackendTranslation(
              securityCountry.name ?? "",
              securityCountry.namesAsMap,
              locale
            ),
          }),
        });
      }

      const securityType = curr.type;
      if (
        securityType &&
        !prev.type.some((type) => type.id === securityType.code)
      ) {
        prev.type.push({
          id: securityType.code,
          label: t(`fundTypes.${securityType.code}`, {
            value: securityType.name,
            defaultValue: getBackendTranslation(
              securityType.name ?? "",
              securityType.namesAsMap,
              locale
            ),
          }),
        });
      }

      const subType = curr.securitySubType;
      if (
        subType &&
        !prev.securitySubType.some(
          (securitySubType) => securitySubType.id === subType.name
        )
      ) {
        prev.securitySubType.push({
          id: subType.name,
          label: t(`fundTypes.${subType.name}`, {
            value: subType.name,
            defaultValue: getBackendTranslation(
              subType.name ?? "",
              subType.namesAsMap,
              locale
            ),
          }),
        });
      }

      return prev;
    }, filterOptionsInitial) ?? filterOptionsInitial;

  return {
    loading,
    error,
    data: data?.securities,
    filters,
    setFilters,
    filterOptions,
  };
};

export const useGetTradebleSecurityLazy = () => {
  const [error, setError] = useState<ApolloError | undefined>();
  const client = useApolloClient();

  const getTradableSecurity = async (variables: OperationVariables) => {
    try {
      const result = await client.query({
        query: TRADABLE_SECURITIES_QUERY,
        variables,
      });

      // Emulate an error for development
      // Uncomment below lines to emulate an error
      //const error = new ApolloError({ errorMessage: "Emulated error" });
      //throw error;

      setError(undefined);
      return result;
    } catch (err) {
      if (err instanceof ApolloError) setError(err);
    }
  };

  return {
    getTradableSecurity,
    error,
  };
};
